import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { HeaderComponent } from '@components/home/header/header.component';
import { SideNavComponent } from '@shared/components/side-nav/side-nav.component';
import { MessageToastComponent } from '@components/home/message-toast/message-toast.component';
import { GlobalService } from '@core/services/global/global.service';
import { Subject, takeUntil, tap, filter } from 'rxjs';
import { OverlaySpinnerComponent } from '@shared/components/overlay-spinner/overlay-spinner.component';
import { setGroupMemberCode, setRole } from '@core/interceptors/fetch-client';
import { isPlatformBrowser } from '@angular/common';
import { ErrorPageComponent } from '@home/src/app/shared/components/app-error-page/app-error-page.component';
import { AuthenticationService } from './core/services/Auth/authservice';
import { CommentsSectionComponent } from './shared/components/comments-section/comments-section.component';
import { SignalrService } from './core/services/signalr/signalr.service';
import { CommentsService } from './core/services/comments/comments.service';
import { LogRocketService } from './core/services/Log/logrocket.service';
import { NotificationSectionComponent } from './shared/components/notification-section/notification-section.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    SideNavComponent,
    MessageToastComponent,
    ErrorPageComponent,
    OverlaySpinnerComponent,
    CommentsSectionComponent,
    NotificationSectionComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  title = 'NADA';
  showOverlaySpinner: boolean = false;
  groupMemberCode: string = '';
  userName: string | null = null;
  isAuthChecked: boolean = false;
  isLoginRoute: boolean = false;
  statusCode!: number;
  showErrorPage: boolean = false;
  claimsData: any;
  authUser: any;

  constructor(
    private globalService: GlobalService,
    private signalrService: SignalrService,
    private commentsService: CommentsService,
    @Inject(PLATFORM_ID) private platformId: object,
    private authService: AuthenticationService,
    private logRocketService: LogRocketService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.logRocketService.init();
    this.authService.isAuthChecked$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isChecked: boolean) => {
        this.isAuthChecked = isChecked;
      });
    this.authService.fincomData$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.authUser = state;
      });

    // Detect if the current route is /login
    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        this.isLoginRoute = url.startsWith('/login');
      });

    if (isPlatformBrowser(this.platformId)) {
      this.claimsData = this.authService.getClaimsData();
      // Localhost override with mockClaims
      if (window.location.hostname === 'localhost') {
        const storedData = sessionStorage.getItem('fincomData');
        const claimsData = storedData ? JSON.parse(storedData) : null;
        this.claimsData = claimsData;
      }
      if (this.claimsData) this.updateClaimsData(this.claimsData);
      const userSession = this.authService.getSessionData();
      if (userSession) {
        if (window.location.hostname === 'localhost') {
          this.userName = userSession;
        } else {
          this.userName = userSession.name;
        }
      }
      await this.getDealers();

      this.signalrService.getSignalRConnection();
      this.commentsService.getAllMessages(this.authUser?.email);
      this.commentsService.getAllNotifications(this.authUser?.email);
      this.commentsService.getGroupMembersList(this.authUser?.email);
      await this.globalService.getOEMDepartments();
    }

    this.globalService.showOverlaySpinner$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((show) => (this.showOverlaySpinner = show)),
      )
      .subscribe();

    this.globalService.updateShowOverlaySpinner(false);
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.complete();
  }

  private updateClaimsData(claimsData: any): void {
    const { committeeCode, positionCode } = claimsData;
    if (!committeeCode) return;
    this.globalService.setSelectedRole(positionCode);
    setGroupMemberCode(committeeCode);
    this.globalService.updateGroupName(committeeCode);
    setRole(positionCode);
  }

  setErrorPage(statusCode: number) {
    this.statusCode = statusCode;
    this.showErrorPage = true;
  }

  resetErrorPage() {
    this.showErrorPage = false;
  }

  async getDealers() {
    this.resetErrorPage();
    try {
      const response: any = await this.globalService.getOrgDetailsByGroupCode();
      if (response?.statusCode !== 200) {
        this.setErrorPage(response.statusCode);
        return;
      }
      if (
        !response?.data?.data ||
        !Array.isArray(response.data.data) ||
        response.data.data.length === 0
      ) {
        this.setErrorPage(501);
        return;
      }
      this.globalService.updateGroupMembers(response.data.data);
      this.globalService.setDefaultDealer(response.data.data);
    } catch (err: any) {
      this.setErrorPage(err?.statusCode || err?.status);
      console.error('Error fetching dealers:', err);
    }
  }
}
